import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Lock, Crown, Zap, ArrowRight, Sparkles } from 'lucide-react';
import { getSubscriptionPrice } from '@/lib/pricing';

export default function ProFeatureGate({
  feature,
  children,
  showUpgrade = true
}) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading || user === null) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const hasAccess = user?.has_pro_access === true || user?.role === 'admin';
  if (hasAccess) return children;

  const trialDaysLeft = user?.trial_days_left || 0;
  const isTrialActive = user?.is_trial_active;
  const subscriptionPrice = getSubscriptionPrice(user);

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <motion.div
        className="max-w-md w-full relative rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e2d5a 50%, #0f172a 100%)',
          boxShadow: '0 0 50px rgba(59,130,246,0.15), 0 0 0 1px rgba(59,130,246,0.2), 0 20px 40px rgba(0,0,0,0.3)',
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Background glow */}
        <div
          className="absolute -top-20 -right-20 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.25) 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 70%)' }}
        />

        <div className="relative z-10 p-8 space-y-6">
          {/* Lock icon */}
          <div className="text-center space-y-3">
            <motion.div
              className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{
                background: 'rgba(59,130,246,0.12)',
                border: '1px solid rgba(59,130,246,0.25)',
                boxShadow: '0 0 20px rgba(59,130,246,0.15)',
              }}
              animate={{ boxShadow: ['0 0 20px rgba(59,130,246,0.15)', '0 0 30px rgba(59,130,246,0.3)', '0 0 20px rgba(59,130,246,0.15)'] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Lock className="h-6 w-6" style={{ color: '#60a5fa' }} />
            </motion.div>

            <div>
              <h3 className="text-xl font-bold text-white font-['Outfit']">
                {feature}
              </h3>
              <p className="text-sm mt-1" style={{ color: 'rgba(147,197,253,0.6)' }}>
                {isTrialActive
                  ? <><strong className="text-blue-300">{trialDaysLeft} days</strong> left in your free trial</>
                  : 'Your free trial has ended — upgrade to keep access'
                }
              </p>
            </div>
          </div>

          {showUpgrade && (
            <>
              {/* Feature list */}
              <div className="space-y-2">
                {[
                  'Full Dashboard Analytics',
                  'Investment & SIP Tracking',
                  'Net Worth Reports',
                  'Advanced Charts',
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2.5 text-sm" style={{ color: 'rgba(226,232,240,0.75)' }}>
                    <div
                      className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(59,130,246,0.18)', border: '1px solid rgba(59,130,246,0.35)' }}
                    >
                      <Sparkles className="h-2.5 w-2.5" style={{ color: '#60a5fa' }} />
                    </div>
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="space-y-2.5">
                <motion.button
                  onClick={() => navigate('/support')}
                  data-testid="pro-gate-upgrade-btn"
                  className="w-full py-3 px-6 rounded-xl font-semibold text-white flex items-center justify-center gap-2 relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #0ea5e9 100%)',
                    boxShadow: '0 0 25px rgba(59,130,246,0.4), 0 4px 15px rgba(59,130,246,0.25)',
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Crown className="h-4 w-4 text-amber-300" />
                  Upgrade to Pro — {subscriptionPrice}
                  <ArrowRight className="h-4 w-4" />
                </motion.button>

                <p className="text-xs text-center" style={{ color: 'rgba(147,197,253,0.4)' }}>
                  Cancel anytime · No hidden fees · 3-month free trial
                </p>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// Trial Banner Component
export function TrialBanner() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) return null;
  if (!user || user.role === 'admin') return null;

  const daysLeft = user.trial_days_left || 0;
  const isTrialActive = user.is_trial_active;
  const subscriptionPrice = getSubscriptionPrice(user);

  if (user.payment_past_due) {
    return (
      <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white px-4 py-1 text-center">
        <div className="flex items-center justify-center gap-2 text-xs">
          <Lock className="h-3 w-3 shrink-0" />
          <span className="font-medium">Payment failed — please update your payment method</span>
          <Button size="sm" variant="secondary" onClick={() => navigate('/support')} className="ml-1 h-5 text-xs px-2 py-0">
            Update Payment
          </Button>
        </div>
      </div>
    );
  }

  if (user.has_pro_access) return null;

  if (isTrialActive === false) {
    return (
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-1 text-center">
        <div className="flex items-center justify-center gap-2 text-xs">
          <Lock className="h-3 w-3 shrink-0" />
          <span className="font-medium">Your trial has ended</span>
          <Button size="sm" variant="secondary" onClick={() => navigate('/support')} className="ml-1 h-5 text-xs px-2 py-0">
            Upgrade — {subscriptionPrice}
          </Button>
        </div>
      </div>
    );
  }

  if (isTrialActive === true && daysLeft >= 0 && daysLeft <= 7) {
    return (
      <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-1 text-center">
        <div className="flex items-center justify-center gap-2 text-xs">
          <Crown className="h-3 w-3 shrink-0" />
          <span className="font-medium">{daysLeft} days left in your trial</span>
          <Button size="sm" variant="secondary" onClick={() => navigate('/support')} className="ml-1 h-5 text-xs px-2 py-0">
            Subscribe Now
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
